import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import InlineConditionPills from 'components/InlineConditionPills/InlineConditionPills';
import InlineFoilPills from 'components/InlineFoilPills/InlineFoilPills';
import InlineLanguagePills from 'components/InlineLanguagePills/InlineLanguagePills';
import SetMultiSelect from './SetMultiSelect';
import RarityPills from './RarityPills';
import { useGetWantListDefaultsQuery } from 'store/services/settings/settingsApi';
import {
  useAddSetToWantListMutation,
  type AddSetToWantListResponse
} from 'store/services/collectionList/wantList/wantListApi';
import type { SetInfo } from 'store/services/sets/setsApi';
import { mapFrontendConditionsToBackend } from 'utils/wantListDefaultsMapper';
import { mapFrontendToBackend as mapFrontendLanguagesToBackend } from 'constants/languages';
import './AddSetModal.css';

interface AddSetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (result: AddSetToWantListResponse) => void;
}

// Available languages for the modal
const AVAILABLE_LANGUAGES = ['EN', 'JA', 'DE', 'FR', 'IT', 'ES', 'PT', 'RU', 'KO', 'ZHS', 'ZHT'];

const AddSetModal: React.FC<AddSetModalProps> = ({ isOpen, onClose, onSuccess }) => {
  // Get user defaults
  const { data: wantListDefaults } = useGetWantListDefaultsQuery();

  // RTK Query mutation
  const [addSetToWantList, { isLoading: isSubmitting }] = useAddSetToWantListMutation();

  // Form state
  const [selectedSets, setSelectedSets] = useState<SetInfo[]>([]);
  const [quantity, setQuantity] = useState<number>(1);
  const [conditions, setConditions] = useState<string[]>(
    wantListDefaults?.defaultConditions || ['NM', 'LP', 'MP']
  );
  const [languages, setLanguages] = useState<string[]>(
    wantListDefaults?.defaultLanguages || ['EN']
  );
  const [acceptFoil, setAcceptFoil] = useState<boolean>(
    wantListDefaults?.defaultAcceptFoil ?? true
  );
  const [acceptNonFoil, setAcceptNonFoil] = useState<boolean>(
    wantListDefaults?.defaultAcceptNonFoil ?? true
  );
  const [rarities, setRarities] = useState<string[]>([]); // empty = all rarities
  const [skipBasicLands, setSkipBasicLands] = useState<boolean>(false);

  // Result state
  const [result, setResult] = useState<AddSetToWantListResponse | null>(null);

  // Calculate total cards preview
  const totalCardsPreview = useMemo(() => {
    return selectedSets.reduce((sum, set) => sum + set.cardCount, 0);
  }, [selectedSets]);

  const handleFoilChange = (newAcceptFoil: boolean, newAcceptNonFoil: boolean) => {
    setAcceptFoil(newAcceptFoil);
    setAcceptNonFoil(newAcceptNonFoil);
  };

  const handleSubmit = async () => {
    if (selectedSets.length === 0) {
      toast.error('Please select at least one set');
      return;
    }

    try {
      const response = await addSetToWantList({
        setCodes: selectedSets.map(s => s.code),
        quantity,
        acceptableConditions: mapFrontendConditionsToBackend(conditions),
        acceptableLanguages: mapFrontendLanguagesToBackend(languages),
        acceptFoil,
        acceptNonFoil,
        skipExisting: true,
        skipBasicLands,
        rarities: rarities.length > 0 ? rarities : undefined, // undefined = all rarities
      }).unwrap();

      setResult(response);

      if (response.cardsAdded > 0) {
        toast.success(response.message);
      }

      onSuccess?.(response);
    } catch (error: any) {
      console.error('Add set error:', error);
      const errorMessage = error?.data?.message || 'Failed to add sets. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleReset = () => {
    setSelectedSets([]);
    setQuantity(1);
    setConditions(wantListDefaults?.defaultConditions || ['NM', 'LP', 'MP']);
    setLanguages(wantListDefaults?.defaultLanguages || ['EN']);
    setAcceptFoil(wantListDefaults?.defaultAcceptFoil ?? true);
    setAcceptNonFoil(wantListDefaults?.defaultAcceptNonFoil ?? true);
    setRarities([]);
    setSkipBasicLands(false);
    setResult(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="add-set-modal-overlay" onClick={handleClose}>
      <div className="add-set-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="add-set-modal-header">
          <h2>Add Set to Want List</h2>
          <button className="add-set-modal-close" onClick={handleClose}>
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        {/* Body */}
        <div className="add-set-modal-body">
          {/* Result State */}
          {result && !isSubmitting ? (
            <div className="add-set-result-section">
              <div className="add-set-result-icon">
                <FontAwesomeIcon icon="check-circle" size="3x" />
              </div>
              <h3>Import Complete!</h3>
              <div className="add-set-result-stats">
                <div className="add-set-result-stat success">
                  <FontAwesomeIcon icon="check" />
                  <span>{result.cardsAdded} cards added</span>
                </div>
                {result.cardsSkipped > 0 && (
                  <div className="add-set-result-stat skipped">
                    <FontAwesomeIcon icon="forward" />
                    <span>{result.cardsSkipped} cards skipped (already in Want List)</span>
                  </div>
                )}
                {result.errors.length > 0 && (
                  <div className="add-set-result-stat error">
                    <FontAwesomeIcon icon="times-circle" />
                    <span>{result.errors.length} errors</span>
                  </div>
                )}
              </div>

              {/* Per-set breakdown */}
              {result.sets.length > 0 && (
                <div className="add-set-result-breakdown">
                  <h4>Sets Added:</h4>
                  {result.sets.map(set => (
                    <div key={set.setCode} className="add-set-result-set">
                      <span className="set-name">{set.setName}</span>
                      <span className="set-count">
                        +{set.cardsAdded} {set.cardsSkipped > 0 && `(${set.cardsSkipped} skipped)`}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Error details */}
              {result.errors.length > 0 && (
                <details className="add-set-result-errors">
                  <summary>View errors</summary>
                  <div className="add-set-error-list">
                    {result.errors.map((err, idx) => (
                      <div key={idx} className="add-set-error-item">
                        {err.cardName && <span className="error-card">{err.cardName}</span>}
                        <span className="error-message">{err.error}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ) : (
            <>
              {/* Set Selection */}
              <SetMultiSelect
                selectedSets={selectedSets}
                onChange={setSelectedSets}
                maxSelections={5}
                disabled={isSubmitting}
              />

              {/* Empty State */}
              {selectedSets.length === 0 && (
                <div className="add-set-empty-state">
                  <FontAwesomeIcon icon="circle-plus" size="2x" />
                  <p>Search for a set above to get started</p>
                </div>
              )}

              {/* Preferences Section - only show when sets selected */}
              {selectedSets.length > 0 && (
                <>
                  <div className="add-set-preferences">
                    <h3>Card Preferences</h3>

                    {/* Condition */}
                    <div className="add-set-preference-row">
                      <label>Condition:</label>
                      <InlineConditionPills
                        value={conditions}
                        onChange={setConditions}
                        disabled={isSubmitting}
                      />
                    </div>

                    {/* Foil */}
                    <div className="add-set-preference-row">
                      <label>Finish:</label>
                      <InlineFoilPills
                        acceptFoil={acceptFoil}
                        acceptNonFoil={acceptNonFoil}
                        onChange={handleFoilChange}
                        disabled={isSubmitting}
                      />
                    </div>

                    {/* Language */}
                    <div className="add-set-preference-row">
                      <label>Language:</label>
                      <InlineLanguagePills
                        value={languages}
                        availableOptions={AVAILABLE_LANGUAGES}
                        onChange={setLanguages}
                        disabled={isSubmitting}
                      />
                    </div>

                    {/* Rarity */}
                    <div className="add-set-preference-row">
                      <RarityPills
                        value={rarities}
                        onChange={setRarities}
                        disabled={isSubmitting}
                      />
                    </div>

                    {/* Quantity */}
                    <div className="add-set-preference-row">
                      <label>Quantity:</label>
                      <select
                        className="add-set-quantity-select"
                        value={quantity}
                        onChange={(e) => setQuantity(Number(e.target.value))}
                        disabled={isSubmitting}
                      >
                        <option value={1}>1x (one of each)</option>
                        <option value={2}>2x (two of each)</option>
                        <option value={3}>3x (three of each)</option>
                        <option value={4}>4x (playset)</option>
                      </select>
                    </div>

                    {/* Basic Lands Toggle */}
                    <div className="add-set-preference-row checkbox-row">
                      <label className="add-set-checkbox-label">
                        <input
                          type="checkbox"
                          checked={skipBasicLands}
                          onChange={(e) => setSkipBasicLands(e.target.checked)}
                          disabled={isSubmitting}
                        />
                        <span>Exclude Basic Lands</span>
                      </label>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="add-set-preview">
                    <FontAwesomeIcon icon="info-circle" />
                    <span>
                      This will add up to <strong>{totalCardsPreview.toLocaleString()}</strong> cards
                      to your Want List
                    </span>
                    <span className="add-set-preview-note">
                      (Duplicates will be skipped)
                    </span>
                  </div>

                  {/* Loading State */}
                  {isSubmitting && (
                    <div className="add-set-progress-section">
                      <div className="add-set-progress-bar">
                        <div className="add-set-progress-fill add-set-progress-indeterminate" />
                      </div>
                      <p className="add-set-progress-text">
                        Adding cards... This may take a moment for large sets.
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="add-set-modal-footer">
          {result ? (
            <>
              <MagicButton
                label="Done"
                color="grey"
                onPress={handleClose}
              />
              <MagicButton
                label="Add More Sets"
                color="purple"
                onPress={handleReset}
              />
            </>
          ) : (
            <>
              <MagicButton
                label="Cancel"
                color="grey"
                onPress={handleClose}
                isDisabled={isSubmitting}
              />
              <MagicButton
                label={`Add ${totalCardsPreview > 0 ? `${totalCardsPreview.toLocaleString()} Cards` : 'Cards'} to Want List`}
                color="purple"
                onPress={handleSubmit}
                isDisabled={isSubmitting || selectedSets.length === 0}
                isLoading={isSubmitting}
              />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddSetModal;
